import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid, GridSortDirection } from '@mui/x-data-grid';
import {
  AcademicYearDTO,
  BeableCourseType,
  LessonHistoryDataRecord,
  LessonSectionStatus,
  LookupUserDTOV1,
  ModifyActivityQuestionScoresResponse,
} from '@beable/roster-backend-client-v1';
import moment from 'moment';
import { getStyles } from '../../styles';
import ModifyScoreDialog from './ModifyScoreDialog';
import ResetLessonDialog from './ResetLessonDialog';

interface UserLessonProgressTableProps {
  profile: LookupUserDTOV1;
  academicYear: AcademicYearDTO;
  languageCode: string;
  lessonProgress?: LessonHistoryDataRecord[];
}

interface LessonTableDataRow {
  id: string;
  courseId: string;
  courseName: string;
  courseType: BeableCourseType;
  lessonName: string;
  lessonLexile: number | undefined;
  startDate: Date | undefined;
  endDate: Date | undefined;
  sections: {
    wordStudy: string;
    backgroundInfo: string;
    coreReading: string;
  };
  sectionStatus: {
    wordStudy: string;
    backgroundInfo: string;
    coreReading: string;
  };
  sectionScore: {
    wordStudy: { score: string | number; activityAttemptId: string };
    backgroundInfo: { score: string | number; activityAttemptId: string };
    coreReading: { score: string | number; activityAttemptId: string };
  };
}

export enum Section {
  coreReading = 'coreReading',
  wordStudy = 'wordStudy',
  backgroundInfo = 'backgroundInfo',
}

export interface ModifyDialogProps {
  lessonId: string;
  activityAttemptId: string;
  lessonName: string;
  courseType: BeableCourseType;
  section: Section;
}

export interface ResetDialogProps {
  userAccountId: string;
  siteId: number;
  academicYearId: string;
  lessonId: string;
  courseId: string;
  lessonName: string;
  languageCode: string;
}

export default function UserLessonProgressData({
  lessonProgress,
  profile,
  academicYear,
  languageCode,
}: UserLessonProgressTableProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [modifyDialogOpen, setModifyDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [rows, setRows] = useState<LessonTableDataRow[]>();
  const [updateResults, setUpdateResults] =
    useState<{
      data: ModifyActivityQuestionScoresResponse;
      lessonId: string;
      section: Section;
    }>();
  const [modifyDialogProps, setModifyDialogProps] = useState<
    ModifyDialogProps | undefined
  >();
  const [resetDialogProps, setResetDialogProps] = useState<
    ResetDialogProps | undefined
  >();
  const [resetLesson, setResetLesson] = useState<{ lessonId: string; courseId: string } | undefined>();
  const rowsPerPage = 30;

  const setRow = useCallback((lesson: LessonHistoryDataRecord) => {
    return {
      id: lesson.lessonId,
      courseId: lesson.courseId,
      courseName: lesson.courseTitle,
      courseType: lesson.courseType,
      lessonName: lesson.lessonTitle,
      lessonLexile: lesson.lessonLexile,
      startDate: lesson.lessonStartAt,
      endDate: lesson.lessonEndAt,
      sections: {
        wordStudy: 'Word Study',
        backgroundInfo: 'Background Info',
        coreReading: 'Core Reading Questions',
      },
      sectionStatus: {
        wordStudy: sectionStatus(lesson.sectionStatus.wordstudy),
        backgroundInfo: sectionStatus(lesson.sectionStatus.background),
        coreReading: sectionStatus(lesson.sectionStatus.corereading),
      },
      sectionScore: {
        wordStudy: sectionScore(lesson.sectionStatus.wordstudy),
        backgroundInfo: sectionScore(lesson.sectionStatus.background),
        coreReading: sectionScore(lesson.sectionStatus.corereading),
      },
    };
  }, []);

  useEffect(() => {
    const rows = lessonProgress && lessonProgress.map((lesson) => setRow(lesson));
    setRows(rows);
  }, [lessonProgress, setRow]);

  const lessonProgressColumnData = [
    { field: 'id', headerName: 'ID' },
    { field: 'courseName', headerName: 'Course', width: 200 },
    {
      field: 'lessonName',
      headerName: 'Lesson',
      width: 200,
      renderCell: (params: any) => (
        <Box sx={styles.progress_div}>
          <Typography sx={styles.lessonTitle_text}>
            {params.value}
          </Typography>
          <LessonResetButton lessonId={params.row.id} lessonName={params.row.lessonName} courseId={params.row.courseId}/>
        </Box>
      ),
    },
    { field: 'lessonLexile', headerName: 'Lexile', width: 90 },
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 150,
      valueGetter: (_value: unknown, row: LessonTableDataRow) =>
        row.startDate ? moment(row.startDate).format('YYYY-MM-DD') : '',
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      width: 150,
      valueGetter: (_value: unknown, row: LessonTableDataRow) =>
        row.endDate ? moment(row.endDate).format('YYYY-MM-DD') : '',
    },
    {
      field: 'sections',
      headerName: 'Section Title',
      width: 200,
      renderCell: (params: any) => (
        <div>
          <Typography sx={styles.progress_text}>
            {params.value.wordStudy}
          </Typography>
          <Typography sx={styles.progress_text}>
            {params.value.backgroundInfo}
          </Typography>
          <Typography sx={styles.progress_text}>
            {params.value.coreReading}
          </Typography>
        </div>
      ),
    },
    {
      field: 'sectionStatus',
      headerName: 'Status',
      width: 140,
      renderCell: (params: any) => (
        <div>
          <Typography sx={styles.progress_text}>
            {params.value.wordStudy}
          </Typography>
          <Typography sx={styles.progress_text}>
            {params.value.backgroundInfo}
          </Typography>
          <Typography sx={styles.progress_text}>
            {params.value.coreReading}
          </Typography>
        </div>
      ),
    },
    {
      field: 'sectionScore',
      headerName: 'Score',
      width: 200,
      renderCell: (params: any) => (
        <div>
          <Typography sx={styles.progress_text}>
            {params.value.wordStudy.score}
            {sectionScoreButton(
              params.row.id,
              params.value.wordStudy.activityAttemptId,
              params.row.lessonName,
              params.row.courseType,
              Section.wordStudy,
            )}
          </Typography>
          <Typography sx={styles.progress_text}>
            {params.value.backgroundInfo.score}
            {sectionScoreButton(
              params.row.id,
              params.value.backgroundInfo.activityAttemptId,
              params.row.lessonName,
              params.row.courseType,
              Section.backgroundInfo,
            )}
          </Typography>
          <Typography sx={styles.progress_text}>
            {params.value.coreReading.score}
            {sectionScoreButton(
              params.row.id,
              params.value.coreReading.activityAttemptId,
              params.row.lessonName,
              params.row.courseType,
              Section.coreReading,
            )}
          </Typography>
        </div>
      ),
    },
  ];

  const sortModel = [
    //community version only allows sorting by single column
    // {
    //   field: 'lessonName',
    //   sort: 'asc' as GridSortDirection,
    // },
    {
      field: 'startDate',
      sort: 'asc' as GridSortDirection,
    },
  ];

  function sectionStatus(sectionStatus: LessonSectionStatus | undefined) {
    if (!sectionStatus) {
      return 'N/A';
    } else if (sectionStatus.endAt) {
      return 'Complete';
    } else if (sectionStatus.startAt) {
      return 'In Progress';
    }
    return 'New';
  }

  function sectionScore(sectionStatus: LessonSectionStatus | undefined) {
    if (sectionStatus && sectionStatus.endAt) {
      return {
        score: `${sectionStatus.score} `,
        activityAttemptId: sectionStatus.activityAttemptId || '',
      };
    }
    return { score: 'N/A', activityAttemptId: '' };
  }

  const closeModifyScoreDialog = () => {
    if (updateResults) {
      setRows((prevRows) => {
        const newRows = prevRows?.map((row) => {
          if(row.id === updateResults.lessonId) {
           const sectionScore = {
            wordStudy: updateResults.section === Section.wordStudy ? { score: updateResults.data.newScore, activityAttemptId: updateResults.data.newActivityAttemptId } : row.sectionScore.wordStudy,
            backgroundInfo: updateResults.section === Section.backgroundInfo ? { score: updateResults.data.newScore, activityAttemptId: updateResults.data.newActivityAttemptId } : row.sectionScore.backgroundInfo,
            coreReading: updateResults.section === Section.coreReading ? { score: updateResults.data.newScore, activityAttemptId: updateResults.data.newActivityAttemptId } : row.sectionScore.coreReading,
           }
           return { ...row, sectionScore }
          } else return row;
        });
        return newRows;
      });
    }

    setModifyDialogOpen(false);
    setUpdateResults(undefined);
  };

  const handleSetNewActivityData = (
    data: ModifyActivityQuestionScoresResponse,
    lessonId: string,
    section: Section,
  ) => {
    setUpdateResults({ data, lessonId, section });
  };

  const handleSetResetLesson = (lessonId: string, courseId: string) => {
    setResetLesson({ lessonId, courseId });
  };

  const LessonResetButton = ({ lessonName, lessonId, courseId }: {lessonName: string; lessonId: string; courseId: string}) => {
    return (
        <Button
          sx={styles.progress_button}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenResetDialog(lessonId, lessonName, courseId);
          }}
        >
          (reset)
        </Button>
    );
  };

  const handleOpenResetDialog = async (
    lessonId: string,
    lessonName: string,
    courseId: string,
  ) => {
    setResetDialogProps({
      lessonId,
      lessonName,
      courseId,
      userAccountId: profile.userAccountId!,
      siteId: profile.siteId,
      academicYearId: academicYear.academicYearId,
      languageCode,
    });
    setResetDialogOpen(true);
  };

  const closeResetDialog = () => {
    if (resetLesson?.lessonId) {
      setRows((prevRows) => {
        return prevRows?.filter((row) => {
          return row.id !== resetLesson.lessonId;
        });
      });
    }
    setResetDialogOpen(false);
    setResetLesson(undefined);
  };

  const sectionScoreButton = (
    lessonId: string,
    activityAttemptId: string,
    lessonName: string,
    courseType: BeableCourseType,
    section: Section,
  ) => {
    return (
      activityAttemptId && (
        <>
          <Button
            sx={styles.progress_button}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenModifyDialog(
                lessonId,
                activityAttemptId,
                lessonName,
                courseType,
                section,
              );
            }}
          >
            (rescore)
          </Button>
        </>
      )
    );
  };

  const handleOpenModifyDialog = async (
    lessonId: string,
    activityAttemptId: string,
    lessonName: string,
    courseType: BeableCourseType,
    section: Section,
  ) => {
    setModifyDialogProps({
      lessonId,
      activityAttemptId,
      lessonName,
      courseType,
      section,
    });
    setModifyDialogOpen(true);
  };

  if (rows?.length) {
    return (
      <Box
        sx={{
          height: 150 + Math.min(rowsPerPage, rows?.length) * 120 + 'px',
        }}
      >
        <DataGrid
          disableRowSelectionOnClick
          initialState={{
            columns: {
              columnVisibilityModel: {
                id: false, // Hidden
                courseName: false,
                lessonName: true, // Visible
                lessonLexile: true,
                startDate: false,
                endDate: false,
                sections: true,
                sectionStatus: true,
                sectionScore: true
              },
            },
          }}
          pageSizeOptions={[rowsPerPage]}
          sortingOrder={['desc', 'asc']}
          sortModel={sortModel}
          rows={rows}
          columns={lessonProgressColumnData}
          autoHeight={false}
          rowHeight={120}
          sx={styles.progress_table}
        />
        <ModifyScoreDialog
          open={modifyDialogOpen}
          props={modifyDialogProps}
          handleClose={closeModifyScoreDialog}
          setNewActivityData={handleSetNewActivityData}
        />
        <ResetLessonDialog
          open={resetDialogOpen}
          props={resetDialogProps}
          handleClose={closeResetDialog}
          setResetLesson={handleSetResetLesson}
        />
      </Box>
    );
  } else return <Typography>No lesson progress found</Typography>;
}
